
import { DelistingFullIcon } from '#delisting/constants'
import { isLive, Query, QueryPill, QueryPillProps, SearchCondition } from '#search/model'
import { useT } from 'apprise-frontend-core/intl/language'
import { jsxutils } from 'apprise-frontend-core/utils/common'
import { FormState, useForm } from 'apprise-frontend-core/utils/form'
import { Button } from 'apprise-ui/button/button'
import { classname } from 'apprise-ui/component/model'
import { SidebarContext } from 'apprise-ui/page/sidebar'
import { Tip } from 'apprise-ui/tooltip/tip'
import { RemoveItemIcon, SaveIconFull } from 'apprise-ui/utils/icons'
import { useContext, useEffect } from 'react'
import { AiFillDelete, AiFillFolderOpen, AiFillPlusCircle } from 'react-icons/ai'
import { BiUndo } from 'react-icons/bi'
import { IoIosPlay, IoIosTime } from 'react-icons/io'
import { startQuery } from './constants'
import './querypanel.scss'
import { useSearch } from './api'
import { usePrevious } from 'apprise-frontend-core/utils/hooks'
import { utils } from 'apprise-core/utils'

export type FilterPanelProps = {

    onChange: (_: Query) => any
    onRefresh: () => any
    onSave: () => any
    form: FormState<Query>

    children: JSX.Element[]
}


export const QueryPanel = (props: FilterPanelProps) => {

    const t = useT()

    const { form, onRefresh: refreshResults, onChange: changeQuery, onSave, children } = props

    const { initial: lastQuery, edited: query, set: setQuery, reset: resetQuery, dirty, rebased, resetSignal } = form

    const { silent = false, page } = useSearch().get()

    const previous = usePrevious({ query, page })

    const pills = jsxutils().elementsIn(children).filter(jsxutils().isElementOf(QueryPill))

    const pillprops = pills.map(c => c.props as QueryPillProps).filter(p => isLive(p, query))

    // a pill declares the mode and records it belongs to, and isLive tells if the edited query covers them. outside them the pill
    // is inert: not offered, greyed if it was already laid out, and contributing no conditions to the search.
    // the layout is not recomputed when a toggle flips, so a pill selected while live stays on screen, greyed.
    const inert = (p: QueryPillProps) => !isLive(p, query)

    // a query's conditions always come with their pill: an inert one is laid out greyed, not left out,
    // or its conditions would turn live, unseen, when the mode flips back.
    const initialLayout = () => pills.map(c => c.props as QueryPillProps).filter(p => p.defaultLayout || query.conditions[p.name])
        // clones pills to match current condition cardinality for repeatable props
        .flatMap(p => query.conditions[p.name] ? query.conditions[p.name].map(_ => ({ ...p })) : [p])

    //console.log({lastQuery, layout: initialLayout()})

    const { edited: selected, reset: resetSelected } = useForm(initialLayout())

    const ctx = useContext(SidebarContext)

    const search = useSearch()

    // populate pill registry on mount with all available pills
    useEffect(() => {
        const registry = pills.reduce((acc, c) => {
            const props = c.props as QueryPillProps
            acc[props.name] = props
            return acc
        }, {} as Record<string, QueryPillProps>)
        search.set(s => { s.pillRegistry = registry })
    // eslint-disable-next-line
    }, [])

    // 'new' query => changed via this panel or reset externally.
    const changedQuery = dirty || rebased

    const updateQuery = () => {

        if (changedQuery)
            onReset(query)

        else refreshResults()  // triggers result refreshes for current query.

    }

    const queryHasEffectivelyChanged = changedQuery && previous && !utils().deepequals(query, previous.query)

    useEffect(() => {
        if (silent && queryHasEffectivelyChanged) 
            updateQuery()
        
        // eslint-disable-next-line
    }, [changedQuery])

    useEffect(() => {

        if (resetSignal)
            resetSelected.to(initialLayout()).quietly()

        // eslint-disable-next-line
    }, [resetSignal])

    const onReset = (query: Query = startQuery) => {

        resetQuery.to(query).quietly() // resets, so future reverts will go back to this query.

        resetQuery.base.quietly()       // unflags rebased, if set.

        changeQuery(query)

        if (query === startQuery)
            resetSelected.to(initialLayout()).quietly()

    }


    // resets to last executed query, but without a reset so that it preserves the current layout.
    const revert = () => setQuery.to(lastQuery)

    const toggleHistoryMode = () => setQuery.using(q => {

        q.mode = q.mode === 'current' ? 'historical' : 'current'

        if (q.mode === 'historical')
            // historical searches include delisted records whatever the toggle says, so we force it off (and disable it).
            // leaving historical therefore always lands with delisted records excluded, which greys the 'delisted' pills.
            q.delisted = 'exclude'
    })

    const toggleDelistedMode = () => setQuery.using(q => q.delisted = q.delisted === 'include' ? 'exclude' : 'include')

    // assumes starts collapsed: must align with defaultOpen prop on search page.
    const toggle = () => ctx.set(s => { s.collapsed = s.collapsed === undefined ? false : !s.collapsed })

    const revertBtn = <Button enabled={dirty} className='form-btn' tip={t('search.query_revert_tip')} tipPlacement='bottom' tipDelay={.7} type='ghost' onClick={revert}>
        <BiUndo color={dirty ? 'lightseagreen' : 'inherit'} />
    </Button>


    const resetBtn = <Button className='form-btn' tip={t('search.query_reset_tip')} tipPlacement='bottom' tipDelay={.7} type='ghost' onClick={() => onReset(undefined!)}>
        <AiFillDelete color='lightseagreen' />
    </Button>

    const saveBtn = <Button className='form-btn' disabled={dirty} tipPlacement='bottom' tip={t('search.query_save_tip')} tipDelay={.7} type='ghost' onClick={onSave}>
        <SaveIconFull color={dirty ? 'inherit' : 'dodgerblue'} />
    </Button>

    const openBtn = <Button className='form-btn' tipPlacement='bottom' tip={t('search.query_open_tip')} tipDelay={.7} type='ghost' onClick={toggle}>
        <AiFillFolderOpen style={{ marginLeft: 3 }} color={ctx.get().collapsed ? 'dodgerblue' : 'hotpink'} />
    </Button>

    const historyModeBtn = <Button className={classname('form-btn', `mode-btn`, 'history-btn', `mode-${query.mode}`)} tipPlacement='bottom' tip={t('search.historical_mode_tip')} tipDelay={.7} type='ghost' onClick={toggleHistoryMode}>
        <IoIosTime color={'dodgerblue'} />
    </Button>

    const delisteModedBtn = <Button disabled={query.mode === 'historical'} className={classname('form-btn', `mode-btn`, 'delisted-btn', `mode-${query.delisted}`)} tipPlacement='bottom' tip={t('search.delisted_mode_tip')} tipDelay={.7} type='ghost' onClick={toggleDelistedMode}>
        <DelistingFullIcon color={'dodgerblue'} />
    </Button>


    const applyBtn = <Button className={classname('form-btn', 'apply-btn', changedQuery && 'btn-highlight')} tip={t('search.apply')} tipPlacement='bottom' tipDelay={.7} type='ghost' onClick={updateQuery}><IoIosPlay style={{ marginLeft: 3 }} size={20} /></Button>

    const selectPill = (pill: QueryPillProps) => resetSelected.to([...selected, pill]).quietly


    const deselectPill = (pill: QueryPillProps, position: number) => () => {

        // console.log({selected, pill, position})

        resetSelected.to(selected.filter(p => p !== pill)).quietly()

        // pill may have been used already, remove corresponding condition from query.
        removeCondition(pill.name, position)
    }

    // add,change or remove the condition at a given pill.
    // the condition is identified by the logical name of the pill and its order among other pills with the same name.
    const changePill = (name: string, order: number) => (condition?: SearchCondition) => {

        if (condition)
            updateCondition(name, order, condition)
        else
            removeCondition(name, order)

    }

    // add/change the condition at a given pill.
    // the condition is identified by the logical name of the pill and its order among other pills with the same name. 
    const updateCondition = (name: string, order: number, condition: SearchCondition) => setQuery.using(query => {

        // console.log("updating", name, "@", order, "with", condition, "current", query.conditions[name])

        if (!query.conditions[name])
            query.conditions[name] = []

        query.conditions[name][order] = condition

    })

    // removes the condition at a given pill.
    // the condition is identified by the logical name of the pill and its order among other pills with the same name. 
    const removeCondition = (name: string, position: number) => setQuery.using(query => {

        // console.log("remove condition", name, "@", position, {conditions: query.conditions[name]})

        query.conditions[name]?.splice(position, 1)

        if (query.conditions[name]?.length === 0)
            delete query?.conditions[name]
    })

    //console.log({ query: query?.conditions, selected })

    return <div className='query-form'>

        <div className='pills-unselected'>
            {pillprops.map(props =>

                <UnselectedQueryPill disabled={inert(props) || (!props.repeatable && selected.some(p => p.name === props.name))} key={props.name} {...props} onSelect={selectPill(props)} />

            )}
        </div>

        <div className='pills-selected'>
            {selected.map(props => {

                // finds the ordinal of this props among those of the same name. 
                const order = selected.filter(ps => ps.name == props.name).findIndex(ps => ps === props)

                // console.log("selected", props.name, "at", order, query)

                return <SelectedQueryPill key={`${resetSignal}-${props.name}-${order}`} {...props} noDeselect={selected.length === 1} disabled={inert(props)} query={query}

                    onChange={changePill(props.name, order)}
                    condition={query?.conditions[props.name]?.[order]}
                    onDeselect={deselectPill(props, order)} />

            })}
        </div>

        <div className='form-controls'>

            <span className={classname('mode-lbl', 'delisted-lbl', query.mode === 'historical' && 'apprise-disabled', `mode-${query.delisted}`)}>{t('search.delisted_mode')}</span>

            {delisteModedBtn}

            {revertBtn}

            {resetBtn}

            {applyBtn}

            {saveBtn}

            {openBtn}

            {historyModeBtn}

            <span className={classname('mode-lbl', 'history-lbl', `mode-${query.mode}`)}>{t('search.historical_mode')}</span>

        </div>

    </div>

}





const SelectedQueryPill = (props: QueryPillProps & {

    //form: FormState<Query>
    disabled: boolean       // the filter is inert: greyed out and contributing nothing.
    query: Query            // the edited query, for fields that depend on its mode, scope, or delisted setting.
    noDeselect: boolean     // the pill cannot be removed, the last one never is.
    onDeselect: () => void
    condition: SearchCondition | undefined,
    onChange: (_: SearchCondition | undefined) => void

}) => {


    const t = useT()

    const { label, title, field: Field, onDeselect, onChange: updateCondition, condition, disabled, query, noDeselect } = props

    const tippedLabel = label ? <Tip tip={t(title)}>{t(label)}</Tip> : undefined

    // the class greys the field for any widget, including one that ignores 'disabled'.
    // the prop adds to that for the widgets that do honour it, which also stop reporting changes.
    return <div className={classname('query-pill-selected', disabled && 'apprise-disabled')} style={{ display: 'flex' }} >
        <Field label={tippedLabel} disabled={disabled} query={query} onChange={updateCondition}>
            {condition}
        </Field>
        <Button disabled={noDeselect} noLabel className='query-pill-selected-close' type='ghost' icon={<RemoveItemIcon size={12} />} onClick={onDeselect} />
    </div >
}


const UnselectedQueryPill = (props: QueryPillProps & {

    onSelect: () => void
    disabled: boolean

}) => {

    const t = useT()

    const { label, title, onSelect, disabled } = props

    return <Button tip={t('search.pill_unselected_tip', { title: t(title) })} tipDelay={0.8} tipPlacement='top' disabled={disabled} iconPlacement='left' icon={<AiFillPlusCircle />} type='ghost' className='query-pill-unselected' size='small' onClick={onSelect}>
        {t(label)}
    </Button>
}